import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Chip,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Collapse,
  IconButton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import TvIcon from '@mui/icons-material/Tv';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SettingsIcon from '@mui/icons-material/Settings';
import Slider from '@mui/material/Slider';
import axios from 'axios';
import ProductHistoryModal from './ProductHistoryModal';
import { MarketFlag } from './FlagIcons';
import GoldIngotLoader from './GoldIngotLoader';

// Market configuration constants
const MARKETS = {
  US: {
    code: 'US',
    name: 'United States',
    domain: 'www.amazon.com',
    currency: { code: 'USD', symbol: '$', decimals: 2 },
    flag: '🇺🇸',
    locale: 'en-US',
  },
  UK: {
    code: 'UK',
    name: 'United Kingdom',
    domain: 'www.amazon.co.uk',
    currency: { code: 'GBP', symbol: '£', decimals: 2 },
    flag: '🇬🇧',
    locale: 'en-GB',
  },
  JP: {
    code: 'JP',
    name: 'Japan',
    domain: 'www.amazon.co.jp',
    currency: { code: 'JPY', symbol: '¥', decimals: 0 },
    flag: '🇯🇵',
    locale: 'ja-JP',
  },
  CA: {
    code: 'CA',
    name: 'Canada',
    domain: 'www.amazon.ca',
    currency: { code: 'CAD', symbol: 'C$', decimals: 2 },
    flag: '🇨🇦',
    locale: 'en-CA',
  },
};

// Helper function to generate Amazon product URL based on market
// This ensures the URL always matches the selected market, not the stored URL
const getProductUrl = (asin, marketCode) => {
  const market = MARKETS[marketCode] || MARKETS.US;
  return `https://${market.domain}/dp/${asin}`;
};

const ProductCards = ({ marketCode = 'US' }) => {
  const [categoriesData, setCategoriesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState('rank');
  const [filterOpen, setFilterOpen] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [viewMode, setViewMode] = useState('card');
  const [mergeCategories, setMergeCategories] = useState(false);
  // Use marketCode from props (passed from route)
  const selectedMarket = marketCode;
  const defaultDisplayCount = 10;
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantData, setVariantData] = useState(null);
  const [variantLoading, setVariantLoading] = useState(false);
  // Keyword Rankings Modal states
  const [keywordModalOpen, setKeywordModalOpen] = useState(false);
  const [keywordData, setKeywordData] = useState(null);
  const [keywordLoading, setKeywordLoading] = useState(false);

  // Carousel/TV mode states
  const [carouselMode, setCarouselMode] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselInterval, setCarouselIntervalState] = useState(10000); // 10 seconds default
  const [carouselTop, setCarouselTop] = useState(5); // Show top N products per category
  const [countdown, setCountdown] = useState(10); // Countdown timer in seconds
  const [carouselSettingsOpen, setCarouselSettingsOpen] = useState(false); // Settings dialog

  useEffect(() => {
    // Read URL parameters on mount (except market, which comes from route)
    const params = new URLSearchParams(window.location.search);
    const categoriesParam = params.get('categories');
    const ratingParam = params.get('rating');
    const sortParam = params.get('sort');
    const viewParam = params.get('view');

    if (categoriesParam) setSelectedCategories(categoriesParam.split(','));
    if (ratingParam) setMinRating(parseFloat(ratingParam));
    if (sortParam) setSortBy(sortParam);
    if (viewParam) setViewMode(viewParam);
    else setViewMode('card');
  }, []);

  useEffect(() => {
    loadProductData();
    // Reset filters when market changes
    setSelectedCategories([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketCode]);

  useEffect(() => {
    filterProducts();
    updateURL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, minRating, sortBy, categoriesData, viewMode, mergeCategories, selectedMarket]);

  // Auto-carousel effect
  const goToNextCategory = useCallback(() => {
    if (filteredData.length > 0) {
      setCarouselIndex(prev => (prev + 1) % filteredData.length);
    }
  }, [filteredData.length]);

  const goToPrevCategory = useCallback(() => {
    if (filteredData.length > 0) {
      setCarouselIndex(prev => (prev - 1 + filteredData.length) % filteredData.length);
    }
  }, [filteredData.length]);

  useEffect(() => {
    if (!carouselMode || filteredData.length === 0) return;

    const timer = setInterval(() => {
      goToNextCategory();
    }, carouselInterval);

    return () => clearInterval(timer);
  }, [carouselMode, carouselInterval, filteredData.length, goToNextCategory]);

  // Countdown timer effect - updates every second
  useEffect(() => {
    if (!carouselMode || filteredData.length === 0) return;

    // Reset countdown when interval changes or category changes
    setCountdown(Math.floor(carouselInterval / 1000));

    const countdownTimer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          return Math.floor(carouselInterval / 1000);
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [carouselMode, carouselInterval, carouselIndex, filteredData.length]);

  // Keyboard navigation for carousel
  useEffect(() => {
    if (!carouselMode) return;

    const handleKeyDown = (e) => {
      // Don't intercept keyboard when settings dialog is open or focusing on form elements
      if (carouselSettingsOpen) return;
      const tagName = e.target.tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || e.target.closest('.MuiSlider-root')) {
        return;
      }

      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goToNextCategory();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevCategory();
      } else if (e.key === 'Escape') {
        setCarouselMode(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [carouselMode, carouselSettingsOpen, goToNextCategory, goToPrevCategory]);

  const loadProductData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/products?marketCode=${selectedMarket}`);

      if (response.data.success) {
        const categoriesWithParsedVariants = response.data.categories.map(category => ({
          ...category,
          products: category.products.map(product => ({
            ...product,
            variants: typeof product.variants === 'string'
              ? JSON.parse(product.variants)
              : (product.variants || [])
          }))
        }));

        setCategoriesData(categoriesWithParsedVariants);
        setFilteredData(categoriesWithParsedVariants);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading product data:', error);
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...categoriesData];

    if (selectedCategories.length > 0) {
      filtered = filtered.filter(cat =>
        selectedCategories.includes(cat.categoryId.toString())
      );
    }

    filtered = filtered.map(cat => ({
      ...cat,
      products: cat.products.filter(product => {
        if (product.rating < minRating) {
          return false;
        }
        return true;
      })
    }));

    const sortFunction = (a, b) => {
      switch (sortBy) {
        case 'rank':
          return a.rank - b.rank;
        case 'price_asc':
          return a.price - b.price;
        case 'price_desc':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'monthly_sales':
          return (b.monthly_sales || 0) - (a.monthly_sales || 0);
        case 'review_count':
          return (b.reviewCount || 0) - (a.reviewCount || 0);
        default:
          return a.rank - b.rank;
      }
    };

    if (mergeCategories) {
      const allProducts = filtered.flatMap(cat => cat.products);
      const sortedProducts = allProducts.sort(sortFunction);

      setFilteredData([{
        categoryId: 'all',
        categoryName: 'All Categories (Merged)',
        categoryNameLocal: 'All Categories (Merged)',
        products: sortedProducts
      }]);
    } else {
      filtered = filtered.map(cat => ({
        ...cat,
        products: cat.products.sort(sortFunction)
      }));
      setFilteredData(filtered);
    }
  };

  const updateURL = () => {
    const params = new URLSearchParams();

    if (selectedMarket !== 'US') {
      params.set('market', selectedMarket);
    }
    if (selectedCategories.length > 0) {
      params.set('categories', selectedCategories.join(','));
    }
    if (minRating > 0) {
      params.set('rating', minRating.toString());
    }
    if (sortBy !== 'rank') {
      params.set('sort', sortBy);
    }
    if (viewMode !== 'card') {
      params.set('view', viewMode);
    }

    const newURL = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.replaceState(null, '', newURL);
  };

  const formatNumber = (num) => {
    if (!num) return 'N/A';
    return num.toLocaleString();
  };

  // Dynamic currency formatting based on market
  const formatCurrency = (num) => {
    if (!num && num !== 0) return 'N/A';
    const market = MARKETS[selectedMarket] || MARKETS.US;
    const { symbol, decimals } = market.currency;
    const formattedNumber = Number(num).toLocaleString(market.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${symbol}${formattedNumber}`;
  };

  const formatShippingFee = (fee, isFree) => {
    if (isFree === true) {
      return 'Free';
    }
    if (fee === null || fee === undefined || fee === '') {
      return 'Unknown';
    }
    const numericFee = Number(fee);
    if (!Number.isFinite(numericFee)) {
      return 'Unknown';
    }
    if (numericFee === 0) {
      return 'Free';
    }
    return formatCurrency(numericFee);
  };

  const getShippingStatus = (fee, isFree) => {
    if (isFree === true || Number(fee) === 0) {
      return { label: 'Free Shipping', color: 'success' };
    }
    if (isFree === false && fee !== null && fee !== undefined && fee !== '') {
      return { label: 'Paid Shipping', color: 'warning' };
    }
    return { label: 'Shipping Unknown', color: 'default' };
  };

  const getDisplayCount = (categoryId, totalCount) => {
    return expandedCategories[categoryId] ? totalCount : defaultDisplayCount;
  };

  const handleToggleExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleProductClick = async (product, event) => {
    if (product.has_variants && product.variants && product.variants.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      setSelectedProduct(product);
      setVariantModalOpen(true);

      setVariantLoading(true);
      try {
        const response = await axios.get(`/api/product-variants/${product.asin}?marketCode=${selectedMarket}`);
        if (response.data.success && response.data.variants.length > 0) {
          setVariantData(response.data);
        } else {
          setVariantData(null);
        }
      } catch (error) {
        console.error('Error loading variant data:', error);
        setVariantData(null);
      }
      setVariantLoading(false);
    }
  };

  const handleCloseVariantModal = () => {
    setVariantModalOpen(false);
    setSelectedProduct(null);
    setVariantData(null);
  };

  const handleOpenHistoryModal = (product, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedProduct(product);
    setHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setSelectedProduct(null);
  };

  // Keyword Rankings Modal handlers
  const handleOpenKeywordModal = async (product, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedProduct(product);
    setKeywordModalOpen(true);
    setKeywordLoading(true);

    try {
      const response = await axios.get(`/api/search-terms/rankings/${product.asin}`, {
        params: { market: selectedMarket, limit: 20 }
      });
      if (response.data.success) {
        setKeywordData(response.data.data);
      } else {
        setKeywordData([]);
      }
    } catch (error) {
      console.error('Error fetching keyword rankings:', error);
      setKeywordData([]);
    } finally {
      setKeywordLoading(false);
    }
  };

  const handleCloseKeywordModal = () => {
    setKeywordModalOpen(false);
    setSelectedProduct(null);
    setKeywordData(null);
  };

  // Helper for keyword rank color
  const getKeywordRankColor = (rank) => {
    if (rank <= 100) return '#4caf50';      // Green - Top 100
    if (rank <= 1000) return '#2196f3';     // Blue - Top 1000
    if (rank <= 10000) return '#ff9800';    // Orange - Top 10000
    return '#9e9e9e';                        // Gray
  };

  // Helper for product position chip
  const getPositionColor = (position) => {
    const colors = {
      1: { bg: '#ffd700', text: '#000' },  // Gold
      2: { bg: '#c0c0c0', text: '#000' },  // Silver
      3: { bg: '#cd7f32', text: '#fff' },  // Bronze
    };
    return colors[position] || { bg: '#e0e0e0', text: '#000' };
  };

  if (loading) {
    return <GoldIngotLoader minHeight={600} />;
  }

  const currentMarket = MARKETS[selectedMarket] || MARKETS.US;

  // Carousel/TV Mode Fullscreen Display
  if (carouselMode && filteredData.length > 0) {
    const currentCategory = filteredData[carouselIndex];
    const topProducts = currentCategory.products.slice(0, carouselTop);

    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#1a1a2e',
          zIndex: 9999,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MarketFlag marketCode={selectedMarket} size={32} />
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{currentMarket.name} Best Sellers</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Countdown Timer */}
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={(countdown / (carouselInterval / 1000)) * 100}
                size={50}
                thickness={4}
                sx={{ color: '#ff9800' }}
              />
              <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>{countdown}s</Typography>
              </Box>
            </Box>
            <Typography variant="h6" sx={{ color: '#ff9800' }}>{carouselIndex + 1} / {filteredData.length}</Typography>
            <IconButton onClick={() => setCarouselSettingsOpen(true)} sx={{ color: 'white' }}><SettingsIcon fontSize="large" /></IconButton>
            <IconButton onClick={() => setCarouselMode(false)} sx={{ color: 'white' }}><FullscreenExitIcon fontSize="large" /></IconButton>
          </Box>
        </Box>

        {/* Category Title */}
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="h3" sx={{ color: '#ff9800', fontWeight: 'bold', mb: 1 }}>{currentCategory.categoryName}</Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Top {carouselTop} Best Sellers
            {currentCategory.lastUpdated && (
              <span style={{ marginLeft: 16, fontSize: '0.8rem', opacity: 0.7 }}>
                (Updated: {new Date(currentCategory.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
              </span>
            )}
          </Typography>
        </Box>

        {/* Products Grid */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', px: 4, pb: 4 }}>
          <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: '1800px' }}>
            {topProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={product.id}>
                <Card sx={{ height: '100%', backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: 3, transition: 'transform 0.3s', '&:hover': { transform: 'scale(1.02)' } }}>
                  <Box sx={{ position: 'relative', paddingTop: '100%', backgroundColor: '#f5f5f5' }}>
                    <Box sx={{ position: 'absolute', top: 12, left: 12, backgroundColor: product.rank === 1 ? '#ffd700' : product.rank === 2 ? '#c0c0c0' : product.rank === 3 ? '#cd7f32' : '#E87722', color: 'white', borderRadius: '50%', width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.2rem', zIndex: 1, boxShadow: 3 }}>#{product.rank}</Box>
                    <CardMedia component="img" loading="lazy" image={product.imageUrl || 'https://via.placeholder.com/400x400?text=No+Image'} alt={product.title} sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', p: 2 }} />
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem', height: 48, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', mb: 1 }}>{product.title}</Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>{formatCurrency(product.price)}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}><Rating value={product.rating} precision={0.1} size="small" readOnly /><Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.85rem' }}>({formatNumber(product.reviewCount)})</Typography></Box>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'success.main', fontWeight: 'bold' }}>Sales: {formatNumber(product.monthly_sales || 0)}</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'primary.main', fontWeight: 'bold' }}>Total: {formatNumber(product.total_sales || 0)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Navigation Controls */}
        <Box sx={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)' }}>
          <IconButton onClick={goToPrevCategory} sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}><NavigateBeforeIcon fontSize="large" /></IconButton>
        </Box>
        <Box sx={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)' }}>
          <IconButton onClick={goToNextCategory} sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}><NavigateNextIcon fontSize="large" /></IconButton>
        </Box>

        {/* Progress Dots */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pb: 2 }}>
          {filteredData.map((_, idx) => (
            <Box key={idx} onClick={() => setCarouselIndex(idx)} sx={{ width: idx === carouselIndex ? 24 : 12, height: 12, borderRadius: 6, backgroundColor: idx === carouselIndex ? '#ff9800' : 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.3s' }} />
          ))}
        </Box>

        {/* Carousel Settings Dialog */}
        <Dialog open={carouselSettingsOpen} onClose={() => setCarouselSettingsOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Carousel Settings</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography gutterBottom>
                Auto-rotate Interval: {carouselInterval / 1000} seconds
              </Typography>
              <Slider
                value={carouselInterval / 1000}
                onChange={(_, value) => setCarouselIntervalState(value * 1000)}
                min={5}
                max={60}
                step={5}
                marks={[
                  { value: 5, label: '5s' },
                  { value: 15, label: '15s' },
                  { value: 30, label: '30s' },
                  { value: 60, label: '60s' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}s`}
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography gutterBottom>
                Products per Category: {carouselTop}
              </Typography>
              <Slider
                value={carouselTop}
                onChange={(_, value) => setCarouselTop(value)}
                min={3}
                max={10}
                step={1}
                marks={[
                  { value: 3, label: '3' },
                  { value: 5, label: '5' },
                  { value: 7, label: '7' },
                  { value: 10, label: '10' },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCarouselSettingsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Responsive breakpoints
  const isMobileView = window.innerWidth < 600;
  const isXsView = window.innerWidth < 450;

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, md: 2 } }}>
      {/* Filter Bar with Market Title */}
      <Paper
        elevation={3}
        sx={{
          position: { xs: 'relative', md: 'sticky' },
          top: { md: 64 },
          zIndex: 100,
          mb: { xs: 1, md: 2 },
          overflow: 'hidden'
        }}
      >
        {/* Header Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            p: { xs: 1, md: 1.5 },
            backgroundColor: '#f5f5f5',
          }}
        >
          {/* Left: Market Title + Filter Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MarketFlag marketCode={selectedMarket} size={isXsView ? 16 : 20} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '1rem' },
                  whiteSpace: 'nowrap'
                }}
              >
                {isXsView ? currentMarket.code : isMobileView ? `${currentMarket.code} Best Sellers` : `${currentMarket.name} Best Sellers`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                ml: { xs: 0, md: 1 }
              }}
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <FilterListIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '0.7rem', md: '0.85rem' },
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                Filters
              </Typography>
              {filterOpen ? <ExpandLessIcon sx={{ fontSize: { xs: 16, md: 20 } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: 16, md: 20 } }} />}
            </Box>
          </Box>

          {/* Right: View Toggle + TV Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { px: { xs: 0.75, md: 1.5 } } }}
            >
              <ToggleButton value="card">
                <ViewModuleIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.8rem' }, ml: 0.5, display: { xs: 'none', sm: 'block' } }}>CARD</Typography>
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.8rem' }, ml: 0.5, display: { xs: 'none', sm: 'block' } }}>LIST</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => setCarouselMode(true)}
              sx={{
                minWidth: { xs: 32, md: 'auto' },
                px: { xs: 0.75, md: 2 }
              }}
            >
              <TvIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
              <Typography sx={{ ml: 0.5, display: { xs: 'none', md: 'block' }, fontSize: '0.8rem' }}>TV</Typography>
            </Button>
          </Box>
        </Box>


        <Collapse in={filterOpen}>
          <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', gap: { xs: 1.5, sm: 2, md: 3 }, flexWrap: 'wrap' }}>
            {/* Category Filter */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200, md: 250 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
              <InputLabel sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>Categories (Multi-Select)</InputLabel>
              <Select
                multiple
                value={selectedCategories}
                onChange={(e) => setSelectedCategories(e.target.value)}
                label="Categories (Multi-Select)"
                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                renderValue={(selected) =>
                  selected.length === 0
                    ? 'All'
                    : `${selected.length} selected`
                }
              >
                {categoriesData.map((cat) => (
                  <MenuItem key={cat.categoryId} value={cat.categoryId.toString()}>
                    {cat.categoryName} ({cat.products.length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Rating Filter */}
            <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 130, md: 200 }, flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' } }}>
              <InputLabel sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>Min Rating</InputLabel>
              <Select
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
                label="Min Rating"
                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              >
                <MenuItem value={0}>All</MenuItem>
                <MenuItem value={4.5}>4.5+</MenuItem>
                <MenuItem value={4.0}>4.0+</MenuItem>
                <MenuItem value={3.5}>3.5+</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By */}
            <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 140, md: 200 }, flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' } }}>
              <InputLabel sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              >
                <MenuItem value="rank">Rank</MenuItem>
                <MenuItem value="price_asc">Price ↑</MenuItem>
                <MenuItem value="price_desc">Price ↓</MenuItem>
                <MenuItem value="rating">Rating</MenuItem>
                <MenuItem value="monthly_sales">Sales</MenuItem>
              </Select>
            </FormControl>

            {/* Merge Categories - hide on very small screens */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', flex: '0 0 auto' }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', md: '0.85rem' }, mr: 0.5 }}>
                Merge:
              </Typography>
              <Switch
                checked={mergeCategories}
                onChange={(e) => setMergeCategories(e.target.checked)}
                color="primary"
                size="small"
              />
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Main Content */}
      <Box>
        {filteredData.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No products found for {currentMarket.name} market.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add categories for this market in Scraper Config to start scraping.
            </Typography>
          </Paper>
        ) : (
          filteredData.map((category, index) => {
            const displayCount = getDisplayCount(category.categoryId, category.products.length);
            const isExpanded = expandedCategories[category.categoryId];
            const hasMore = category.products.length > defaultDisplayCount;

            return (
              <Accordion
                key={category.categoryId}
                defaultExpanded={true}
                sx={{ mb: 1, boxShadow: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                  sx={{
                    backgroundColor: '#f5f5f5',
                    '&:hover': { backgroundColor: '#e8e8e8' },
                    minHeight: 40,
                    '& .MuiAccordionSummary-content': { my: 0.5 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{
                        backgroundColor: '#E87722',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        height: 24,
                        minWidth: 32
                      }}
                    />
                    {category.categoryUrl ? (
                      <Link
                        href={category.categoryUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '0.95rem',
                          textDecoration: 'none',
                          color: 'inherit',
                          '&:hover': { textDecoration: 'underline', color: '#E87722' }
                        }}
                      >
                        {category.categoryName}
                      </Link>
                    ) : (
                      <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                        {category.categoryName}
                      </Typography>
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                      {category.lastUpdated && (
                        <Chip
                          label={`Updated: ${new Date(category.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e0e0e0' }}
                        />
                      )}
                      <Chip
                        label={`${category.products.length} Products`}
                        color="primary"
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 1, backgroundColor: '#fafafa' }}>
                  {category.products.length === 0 ? (
                    <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No products match the filter conditions
                    </Typography>
                  ) : viewMode === 'list' ? (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Rank</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Image</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5, minWidth: 250 }}>Product</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Price</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Shipping</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Main Sales</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Total Sales</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Rating</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>ASIN</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {category.products.slice(0, displayCount).map((product) => (
                              <TableRow
                                key={product.id}
                                sx={{
                                  '&:hover': { backgroundColor: '#f5f5f5' },
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  if (product.has_variants && product.variants && product.variants.length > 0) {
                                    handleProductClick(product, e);
                                  } else {
                                    window.open(getProductUrl(product.asin, selectedMarket), '_blank');
                                  }
                                }}
                              >
                                <TableCell>
                                  <Chip
                                    label={`#${product.rank}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: product.rank === 1 ? '#ffd700' :
                                        product.rank === 2 ? '#c0c0c0' :
                                        product.rank === 3 ? '#cd7f32' : '#E87722',
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <img
                                    src={product.imageUrl || 'https://via.placeholder.com/50x50?text=No+Image'}
                                    alt={product.title}
                                    loading="lazy"
                                    style={{ width: 45, height: 45, objectFit: 'contain' }}
                                    onError={(e) => {
                                      if (!e.target.dataset.errored) {
                                        e.target.dataset.errored = 'true';
                                        e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Link
                                    href={getProductUrl(product.asin, selectedMarket)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                      fontSize: '0.75rem',
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      '&:hover': { textDecoration: 'underline', color: '#E87722' }
                                    }}
                                  >
                                    {product.title}
                                  </Link>
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                    {formatCurrency(product.price)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Chip
                                    label={formatShippingFee(product.shipping_fee, product.is_free_shipping)}
                                    size="small"
                                    color={getShippingStatus(product.shipping_fee, product.is_free_shipping).color}
                                    sx={{ fontSize: '0.65rem', height: 18 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'success.main', cursor: 'pointer' }} onClick={(e) => handleOpenHistoryModal(product, e)}>
                                      {formatNumber(product.monthly_sales || 0)}
                                    </Typography>
                                    <IconButton size="small" onClick={(e) => handleOpenHistoryModal(product, e)} sx={{ p: 0.2 }}>
                                      <HistoryIcon sx={{ fontSize: 12, color: '#E87722' }} />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'primary.main' }}>
                                    {formatNumber(product.total_sales || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <Rating value={product.rating} precision={0.1} size="small" readOnly sx={{ fontSize: '0.9rem' }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                                      {product.rating}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>
                                      {product.asin}
                                    </Typography>
                                    <IconButton size="small" onClick={(e) => handleOpenKeywordModal(product, e)} sx={{ p: 0.2 }} title="Keyword Rankings">
                                      <SearchIcon sx={{ fontSize: 12, color: '#2196f3' }} />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {hasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleToggleExpand(category.categoryId)}
                            sx={{ fontSize: '0.875rem', px: 3 }}
                          >
                            {isExpanded ? 'Show Less' : `Show More (${category.products.length} total)`}
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      <Grid container spacing={3}>
                        {category.products.slice(0, displayCount).map((product) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={product.id}>
                            <Card
                              onClick={(e) => {
                                if (product.has_variants && product.variants && product.variants.length > 0) {
                                  handleProductClick(product, e);
                                } else {
                                  window.open(getProductUrl(product.asin, selectedMarket), '_blank');
                                }
                              }}
                              sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s',
                                textDecoration: 'none',
                                color: 'inherit',
                                cursor: 'pointer',
                                userSelect: 'text',
                                '&:hover': {
                                  transform: 'translateY(-8px)',
                                  boxShadow: 8
                                }
                              }}
                            >
                              {/* Rank Badge */}
                              <Box
                                sx={{
                                  position: 'relative',
                                  paddingTop: '100%',
                                  backgroundColor: '#f5f5f5'
                                }}
                              >
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    backgroundColor: product.rank === 1 ? '#ffd700' :
                                      product.rank === 2 ? '#c0c0c0' :
                                        product.rank === 3 ? '#cd7f32' : '#E87722',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    zIndex: 1,
                                    boxShadow: 2,
                                  }}
                                >
                                  #{product.rank}
                                </Box>

                                <CardMedia
                                  component="img"
                                  loading="lazy"
                                  image={product.imageUrl || 'https://via.placeholder.com/400x400?text=No+Image'}
                                  alt={product.title}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    p: 2
                                  }}
                                  onError={(e) => {
                                    if (!e.target.dataset.errored) {
                                      e.target.dataset.errored = 'true';
                                      e.target.src = 'https://via.placeholder.com/400x400?text=No+Image';
                                    }
                                  }}
                                />
                              </Box>

                              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                {/* Title */}
                                <Typography
                                  variant="h6"
                                  sx={{
                                    mb: 1.5,
                                    height: 50,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    lineHeight: 1.3
                                  }}
                                >
                                  {product.title}
                                </Typography>

                                {/* Price */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                    {formatCurrency(product.price)}
                                  </Typography>
                                </Box>

                                {/* Rating */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                  <Rating
                                    value={product.rating}
                                    precision={0.1}
                                    size="small"
                                    readOnly
                                  />
                                  <Typography variant="body2" sx={{ ml: 1, fontSize: '0.85rem' }}>
                                    {product.rating} ({formatNumber(product.reviewCount)})
                                  </Typography>
                                </Box>

                                {/* Shipping Information */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <LocalShippingIcon sx={{ fontSize: 16, color: getShippingStatus(product.shipping_fee, product.is_free_shipping).color === 'success' ? '#2e7d32' : '#ed6c02', mr: 0.5 }} />
                                  <Chip
                                    label={formatShippingFee(product.shipping_fee, product.is_free_shipping)}
                                    size="small"
                                    color={getShippingStatus(product.shipping_fee, product.is_free_shipping).color}
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>

                                {/* Sales Information */}
                                <Box sx={{ mb: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      Main Sales:
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" fontWeight="bold" color="success.main" sx={{ fontSize: '0.7rem', cursor: 'pointer' }} onClick={(e) => handleOpenHistoryModal(product, e)}>
                                        {formatNumber(product.monthly_sales || 0)}
                                      </Typography>
                                      <IconButton size="small" onClick={(e) => handleOpenHistoryModal(product, e)} sx={{ p: 0.3 }}>
                                        <HistoryIcon sx={{ fontSize: 14, color: '#E87722' }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      Total Sales:
                                    </Typography>
                                    <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ fontSize: '0.7rem' }}>
                                      {formatNumber(product.total_sales || 0)}
                                    </Typography>
                                  </Box>
                                  {product.has_variants && product.variants && product.variants.length > 0 && (
                                    <Box sx={{ mt: 0.5, p: 0.5, backgroundColor: '#FFF3E0', borderRadius: 0.5 }}>
                                      <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
                                        Click to view {product.variant_count} variant details
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                {/* ASIN with Keyword Icon */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                    ASIN: {product.asin}
                                  </Typography>
                                  <IconButton size="small" onClick={(e) => handleOpenKeywordModal(product, e)} sx={{ p: 0.3 }} title="Keyword Rankings">
                                    <SearchIcon sx={{ fontSize: 14, color: '#2196f3' }} />
                                  </IconButton>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                      {hasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => handleToggleExpand(category.categoryId)}
                            sx={{ fontSize: '0.875rem', px: 4, py: 1 }}
                          >
                            {isExpanded ? 'Show Less' : `Show More (${category.products.length} total)`}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>

      {/* Historical Trend Modal */}
      <ProductHistoryModal
        open={historyModalOpen}
        onClose={handleCloseHistoryModal}
        product={selectedProduct}
        marketCode={selectedMarket}
      />

      {/* Variant Details Modal */}
      <Dialog
        open={variantModalOpen}
        onClose={handleCloseVariantModal}
        maxWidth="md"
        fullWidth
      >
        {selectedProduct && (
          <>
            <DialogTitle>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Variant Sales Details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  {selectedProduct.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  ASIN: {selectedProduct.asin}
                </Typography>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent>
              {/* Summary */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Main Sales (Main Variant)
                    </Typography>
                    <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(selectedProduct.monthly_sales || 0)}
                    </Typography>
                    {selectedProduct.monthly_sales_text && (
                      <Typography variant="caption" color="text.secondary">
                        {selectedProduct.monthly_sales_text}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Total Sales (All Variants)
                    </Typography>
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(selectedProduct.total_sales || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Variant Count
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {selectedProduct.variant_count || 0} variants
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Variant List */}
              {variantLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={40} />
                  <Typography sx={{ ml: 2 }}>Loading variants...</Typography>
                </Box>
              ) : (variantData?.variants?.length > 0 || selectedProduct.variants?.length > 0) ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>ASIN</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Attributes</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>State</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Sales</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Sales Text</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(variantData?.variants || selectedProduct.variants).map((variant, index) => {
                        const asin = variant.variantAsin || variant.asin;
                        const sales = variant.monthlySales ?? variant.sales ?? 0;
                        const salesText = variant.monthlySalesText || variant.sales_text;
                        const price = variant.price;
                        const isMain = variant.isMain || variant.is_main;
                        const state = variant.state || 'AVAILABLE';
                        const attributes = variant.attributes || {};

                        return (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: isMain ? '#fff3e0' :
                              state === 'UNAVAILABLE' ? '#ffebee' : 'inherit',
                            '&:hover': {
                              backgroundColor: isMain ? '#ffe0b2' :
                                state === 'UNAVAILABLE' ? '#ffcdd2' : '#f5f5f5'
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {index + 1}
                              {isMain && (
                                <Chip label="Main" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {asin ? (
                              <Link
                                href={`https://${currentMarket.domain}/dp/${asin}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  textDecoration: 'none',
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {asin}
                              </Link>
                            ) : (
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {attributes && Object.keys(attributes).length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {Object.entries(attributes).map(([key, value]) => (
                                  <Chip
                                    key={key}
                                    label={`${key}: ${value}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                No attribute info
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={state === 'SELECTED' ? 'AVAILABLE' : state}
                              size="small"
                              color={state === 'UNAVAILABLE' ? 'error' : 'success'}
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'bold',
                                color: sales > 0 ? 'success.main' : 'text.secondary',
                                fontSize: '0.85rem'
                              }}
                            >
                              {formatNumber(sales)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {salesText || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {(() => {
                                if (price) {
                                  return formatCurrency(parseFloat(price));
                                }
                                if (isMain && selectedProduct?.price) {
                                  return formatCurrency(parseFloat(selectedProduct.price));
                                }
                                return '-';
                              })()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No variant data
                </Typography>
              )}
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button onClick={handleCloseVariantModal}>Close</Button>
              <Button
                variant="contained"
                href={getProductUrl(selectedProduct.asin, selectedMarket)}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Product Page
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Keyword Rankings Modal */}
      <Dialog
        open={keywordModalOpen}
        onClose={handleCloseKeywordModal}
        maxWidth="md"
        fullWidth
      >
        {selectedProduct && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SearchIcon sx={{ color: '#2196f3' }} />
                <Typography variant="h6" component="span">Keyword Rankings</Typography>
                <Chip
                  label={selectedProduct.asin}
                  size="small"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    backgroundColor: '#ff9800',
                    color: 'white'
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedProduct.title}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {keywordLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : keywordData && keywordData.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: 50 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Search Term</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Search Rank</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Position</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Click %</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Conv %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {keywordData.map((term, idx) => (
                        <TableRow
                          key={idx}
                          sx={{
                            '&:hover': { backgroundColor: '#fafafa' },
                            '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                          }}
                        >
                          <TableCell sx={{ color: '#666' }}>{idx + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {term.search_term}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`#${term.search_frequency_rank?.toLocaleString()}`}
                              size="small"
                              sx={{
                                backgroundColor: getKeywordRankColor(term.search_frequency_rank),
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`#${term.product_position}`}
                              size="small"
                              sx={{
                                backgroundColor: getPositionColor(term.product_position).bg,
                                color: getPositionColor(term.product_position).text,
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 20
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: '#2196f3', fontWeight: 500 }}>
                              {parseFloat(term.click_share || 0).toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 500 }}>
                              {parseFloat(term.conversion_share || 0).toFixed(1)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No keyword ranking data available for this ASIN.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Add this ASIN to the Search Terms monitor to track keywords.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button onClick={handleCloseKeywordModal}>Close</Button>
              <Button
                variant="contained"
                href={`/search-terms`}
                sx={{ backgroundColor: '#2196f3' }}
              >
                View All Keywords
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default ProductCards;
